/**
 * Choosing where a consultation's WebRTC media is hosted.
 *
 * The Chime SDK lets the control plane sit in one region while the media for a
 * given meeting is pinned to another, so where the API happens to run does not
 * decide where the audio and video travel.
 *
 * Media follows the *patient*, not the specialist. A Canadian patient's
 * consultation should not transit the United States merely because the
 * reviewing specialist is in Chennai - the patient is the person whose health
 * information the residency rules exist to protect. Under PHIPA that is the
 * difference between a domestic session and a cross-border disclosure.
 */
/** Regions Chime SDK will host meeting media in. */
export const MEDIA_REGIONS = [
    "af-south-1",
    "ap-northeast-1",
    "ap-northeast-2",
    "ap-south-1",
    "ap-southeast-1",
    "ap-southeast-2",
    "ca-central-1",
    "eu-central-1",
    "eu-north-1",
    "eu-south-1",
    "eu-west-1",
    "eu-west-2",
    "eu-west-3",
    "sa-east-1",
    "us-east-1",
    "us-east-2",
    "us-west-1",
    "us-west-2",
    "il-central-1",
];
export function isMediaRegion(value) {
    return MEDIA_REGIONS.includes(value);
}
/**
 * Every Canadian IANA zone. Listed exhaustively rather than matched on a
 * prefix, because "America/" covers most of two continents and getting a
 * Canadian patient routed to us-east-1 is precisely the mistake this exists
 * to prevent.
 */
const CANADIAN_ZONES = new Set([
    "America/Toronto",
    "America/Montreal",
    "America/Vancouver",
    "America/Edmonton",
    "America/Winnipeg",
    "America/Halifax",
    "America/Moncton",
    "America/St_Johns",
    "America/Regina",
    "America/Swift_Current",
    "America/Whitehorse",
    "America/Yellowknife",
    "America/Iqaluit",
    "America/Rankin_Inlet",
    "America/Resolute",
    "America/Inuvik",
    "America/Dawson",
    "America/Dawson_Creek",
    "America/Fort_Nelson",
    "America/Creston",
    "America/Cambridge_Bay",
    "America/Glace_Bay",
    "America/Goose_Bay",
    "America/Blanc-Sablon",
    "America/Atikokan",
    "America/Nipigon",
    "America/Thunder_Bay",
    "America/Rainy_River",
    "America/Pangnirtung",
    "Canada/Atlantic",
    "Canada/Central",
    "Canada/Eastern",
    "Canada/Mountain",
    "Canada/Newfoundland",
    "Canada/Pacific",
    "Canada/Saskatchewan",
    "Canada/Yukon",
]);
const ZONE_TO_REGION = {
    "Asia/Kolkata": "ap-south-1",
    "Asia/Calcutta": "ap-south-1",
    "Asia/Dubai": "ap-south-1",
    "Asia/Karachi": "ap-south-1",
    "Asia/Colombo": "ap-south-1",
    "Asia/Dhaka": "ap-south-1",
    "Asia/Singapore": "ap-southeast-1",
    "Asia/Bangkok": "ap-southeast-1",
    "Asia/Kuala_Lumpur": "ap-southeast-1",
    "Asia/Manila": "ap-southeast-1",
    "Asia/Jakarta": "ap-southeast-1",
    "Asia/Tokyo": "ap-northeast-1",
    "Asia/Seoul": "ap-northeast-2",
    "Asia/Istanbul": "eu-central-1",
    "Europe/Istanbul": "eu-central-1",
    "Asia/Jerusalem": "il-central-1",
    "Asia/Tel_Aviv": "il-central-1",
    "Australia/Sydney": "ap-southeast-2",
    "Australia/Melbourne": "ap-southeast-2",
    "Pacific/Auckland": "ap-southeast-2",
};
const PREFIX_TO_REGION = [
    ["Europe/", "eu-west-1"],
    ["Africa/", "af-south-1"],
    ["Australia/", "ap-southeast-2"],
    ["Pacific/", "ap-southeast-2"],
    ["Asia/", "ap-south-1"],
    ["Atlantic/", "eu-west-1"],
    ["Indian/", "ap-south-1"],
];
/** South American zones, so they do not fall through to a US region. */
const SOUTH_AMERICAN_ZONES = new Set([
    "America/Sao_Paulo",
    "America/Argentina/Buenos_Aires",
    "America/Santiago",
    "America/Bogota",
    "America/Lima",
    "America/Caracas",
    "America/Montevideo",
    "America/La_Paz",
    "America/Asuncion",
    "America/Guayaquil",
]);
/**
 * Picks the media region for a participant in `timeZone`.
 *
 * `fallback` is used when the zone is unrecognised, so an unusual zone
 * degrades to the configured default rather than throwing mid-booking.
 */
export function mediaRegionForTimeZone(timeZone, fallback) {
    if (timeZone === undefined || timeZone.trim() === "")
        return fallback;
    const zone = timeZone.trim();
    if (CANADIAN_ZONES.has(zone))
        return "ca-central-1";
    if (SOUTH_AMERICAN_ZONES.has(zone))
        return "sa-east-1";
    const exact = ZONE_TO_REGION[zone];
    if (exact !== undefined)
        return exact;
    for (const [prefix, region] of PREFIX_TO_REGION) {
        if (zone.startsWith(prefix))
            return region;
    }
    if (zone === "UTC" || zone === "Etc/UTC")
        return fallback;
    // Remaining America/* zones are North and Central American.
    if (zone.startsWith("America/") || zone.startsWith("US/"))
        return "us-east-1";
    return fallback;
}
/**
 * Whether a consultation's media stays inside Canada. Surfaced in the UI so a
 * coordinator can see it, and the honest answer when an investor asks where
 * the data goes.
 */
export function isDomesticCanadian(region) {
    return region === "ca-central-1";
}
//# sourceMappingURL=media.js.map